import React from 'react';
import { Dropdown, Button, Space } from 'antd';
import { GlobalOutlined, CheckOutlined } from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../../i18n';

interface LanguageSwitcherProps {
  /** Display mode */
  mode?: 'dropdown' | 'inline';
  /** Show language name or just icon */
  showLabel?: boolean;
  /** Button size */
  size?: 'small' | 'middle' | 'large';
  /** Custom className */
  className?: string;
  /** Custom button style */
  buttonStyle?: React.CSSProperties;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  mode = 'dropdown',
  showLabel = true,
  size = 'middle',
  className,
  buttonStyle,
}) => {
  const { i18n } = useTranslation();
  const currentLang = i18n.language;

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
  };

  // Get current language info
  const currentLangInfo =
    supportedLanguages.find(
      lang =>
        lang.code === currentLang ||
        currentLang.startsWith(lang.code.split('-')[0])
    ) || supportedLanguages[0];

  if (mode === 'inline') {
    return (
      <Space className={className} wrap>
        {supportedLanguages.map(lang => (
          <Button
            key={lang.code}
            type={
              currentLang === lang.code ||
              currentLang.startsWith(lang.code.split('-')[0])
                ? 'primary'
                : 'default'
            }
            size={size}
            onClick={() => handleLanguageChange(lang.code)}
          >
            {lang.flag} {lang.name}
          </Button>
        ))}
      </Space>
    );
  }

  // Dropdown mode
  const menuItems: MenuProps['items'] = supportedLanguages.map(lang => ({
    key: lang.code,
    label: (
      <Space>
        <span>{lang.flag}</span>
        <span>{lang.name}</span>
        {(currentLang === lang.code ||
          currentLang.startsWith(lang.code.split('-')[0])) && (
          <CheckOutlined style={{ color: '#1890ff' }} />
        )}
      </Space>
    ),
    onClick: () => handleLanguageChange(lang.code),
  }));

  return (
    <Dropdown
      menu={{ items: menuItems }}
      placement="bottomRight"
      trigger={['click']}
    >
      <Button
        type="text"
        size={size}
        className={className}
        style={buttonStyle}
        icon={<GlobalOutlined />}
      >
        {showLabel && (
          <span style={{ marginLeft: 4 }}>{currentLangInfo.name}</span>
        )}
      </Button>
    </Dropdown>
  );
};

export default LanguageSwitcher;
